/**
 * Cloudflare Images delivery URLs: https://imagedelivery.net/<account>/<image id>/<variant>
 *
 * The catalog publishes the 'small' named variant of each meal photo (the api picks it on
 * upload). The variant on the end can be swapped for another named one, or for a flexible
 * variant made of resize options ('w=1600,q=90'); flexible variants are on for the account
 * (fed-web-3's card images use them). Flexible resizes never upscale (fit defaults to
 * scale-down), so a big width with q=100 serves the original at its own size.
 */
const HOST = 'imagedelivery.net';

/**
 * the account's named variants and the size each is configured for (fit within; a smaller original
 * stays its size). 'public' is left out: it is a copy of 'small'
 */
export const NAMED_VARIANTS = [
  {name: 'large', size: '2048×2048'},
  {name: 'small', size: '1366×768'},
  {name: 'thumb', size: '300×200'},
  {name: 'thumbsquare', size: '200×200'},
];

export const FIT_OPTIONS = ['scale-down', 'contain', 'cover', 'crop', 'pad'];
export const FORMAT_OPTIONS = ['auto', 'jpeg', 'png', 'webp', 'avif'];

/** the named variants as one-click presets ('small' is the url as published in the catalog), then custom resize options */
export const PRESETS = [
  ...NAMED_VARIANTS.map(({name, size}) => ({
    key: name,
    label: `${name} (${size}${name === 'small' ? ', as published' : ''})`,
    short: `${name} ${size}`,
    variant: name,
  })),
  {key: 'custom', label: 'Custom'},
];

/** {account, imageId, variant} of a delivery url, or null for any other url */
export function parseCloudflareImage(url) {
  if (!url || !`${url}`.includes(`${HOST}/`)) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== HOST) return null;
    const [account, imageId, ...variant] = parsed.pathname.split('/').filter(Boolean);
    if (!account || !imageId) return null;
    return {account, imageId, variant: variant.join('/') || ''};
  } catch (e) {
    return null;
  }
}

/** the same image with another variant on the end; a url that is not a delivery url is returned as is */
export function cloudflareImageUrl(url, variant) {
  const image = parseCloudflareImage(url);
  if (!image || !variant) return url;
  return `https://${HOST}/${image.account}/${image.imageId}/${variant}`;
}

const OPTION_KEYS = [
  ['width', 'w'],
  ['height', 'h'],
  ['quality', 'q'],
  ['fit', 'fit'],
  ['format', 'f'],
  ['sharpen', 'sharpen'],
  ['blur', 'blur'],
  ['dpr', 'dpr'],
];

function isSet(value) {
  return value !== null && value !== undefined && `${value}`.trim() !== '';
}

/** 'w=1600,q=90,f=auto' from {width, height, quality, fit, format, sharpen, blur, dpr}; blank fields are left out */
export function flexibleVariant(options = {}) {
  return OPTION_KEYS
    .filter(([key]) => isSet(options[key]))
    .filter(([key]) => !(key === 'format' && options[key] === 'auto' && !options.forceFormat))
    .map(([key, param]) => `${param}=${`${options[key]}`.trim()}`)
    .join(',');
}

/** {width, quality, ...} from 'w=1600,q=90'; unknown parts are dropped */
export function parseFlexibleVariant(variant) {
  const options = {};
  for (const part of `${variant || ''}`.split(',')) {
    const [param, value] = part.split('=');
    const option = OPTION_KEYS.find(([, name]) => name === param);
    if (option && value !== undefined && value !== '') options[option[0]] = value;
  }
  return options;
}

/** a named variant ('large') rather than resize options ('w=1600,q=90') */
export function isNamedVariant(variant) {
  return !!variant && !`${variant}`.includes('=');
}

/** a small, quick-loading version for a thumbnail grid */
export function thumbnailUrl(url, width = 320) {
  return cloudflareImageUrl(url, `w=${width},q=70`);
}
